import {
  useBookmarkedAyahIds,
  useToggleBookmark,
} from '@/hooks/useBookmarks';
import { useSession } from '@/hooks/useSession';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils/cn';

type BookmarkButtonProps = {
  ayahId: number;
  surahId: number;
  ayahNumber: number;
};

export function BookmarkButton({
  ayahId,
  surahId,
  ayahNumber,
}: BookmarkButtonProps) {
  const session = useSession();
  const ids = useBookmarkedAyahIds();
  const toggle = useToggleBookmark();
  const location = useLocation();
  const label = `${surahId}:${ayahNumber}`;

  if (!session.data) {
    const next = encodeURIComponent(`${location.pathname}${location.search}`);
    return (
      <Link
        to={`/login?next=${next}`}
        aria-label={`Sign in to bookmark ayah ${label}`}
        className="inline-flex items-center justify-center h-7 w-7 rounded-md text-ink-300 dark:text-ink-600 hover:text-ink-500 dark:hover:text-ink-400 transition-colors"
      >
        <BookmarkIcon filled={false} />
      </Link>
    );
  }

  const bookmarked = ids.data?.has(ayahId) ?? false;

  // Decide the action HERE, at the render point — before the mutation
  // touches the cache. If we computed this inside mutationFn we'd be
  // reading the already-optimistically-updated state and inverting every
  // toggle. See useToggleBookmark for the underlying constraint.
  function handleClick() {
    toggle.mutate({
      ayahId,
      surahId,
      ayahNumber,
      action: bookmarked ? 'remove' : 'add',
    });
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={toggle.isPending}
      aria-pressed={bookmarked}
      aria-label={
        bookmarked
          ? `Remove bookmark on ayah ${label}`
          : `Bookmark ayah ${label}`
      }
      className={cn(
        'inline-flex items-center justify-center h-7 w-7 rounded-md',
        'transition-colors duration-200 ease-smooth',
        bookmarked
          ? 'text-accent hover:text-accent-600'
          : 'text-ink-300 dark:text-ink-600 hover:text-ink-700 dark:hover:text-ink-300',
        'disabled:opacity-50',
      )}
    >
      <BookmarkIcon filled={bookmarked} />
    </button>
  );
}

function BookmarkIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      aria-hidden="true"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
    </svg>
  );
}
